import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Input } from '@heartinternet/hi-ui';
import { Textarea } from '@heartinternet/hi-ui';
import { Checkbox } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Notice } from '@heartinternet/hi-ui';
import { Badge } from '@heartinternet/hi-ui';
import { Spinner } from '@heartinternet/hi-ui';
import { Icon } from '@heartinternet/hi-ui';
import { Flex } from '@heartinternet/hi-ui';
import { FilterableSelect } from '@heartinternet/hi-ui';
import { Form } from '@heartinternet/hi-ui';
import { FormField } from '@heartinternet/hi-ui';
import { Modal } from '@heartinternet/hi-ui';
import { ConfirmationDialog } from '@heartinternet/hi-ui';
import './TicketRaisePage.css';

export interface ServiceOption {
  /** Value submitted with form, e.g. "package:example.com", "billing_enquiries" */
  value: string;
  /** Display label */
  label: string;
  /** Optgroup heading */
  group: string;
  /** Whether this VPS is managed (suppresses VPS warning) */
  managed?: boolean;
  /** Server names with known issues (for system status prompt) */
  problemServers?: string[];
  /** Show expert services section when selected */
  forExpertServices?: boolean;
  /** Package ID for countdown and error log fetch */
  packageId?: string;
  /** Service type: "ds", "vps", "linux", "windows", etc. */
  serviceType?: string;
}

export interface ExpertServiceOption {
  id: string;
  name: string;
}

export interface SystemStatusMessage {
  subject: string;
  message: string;
}

export interface SupportArticle {
  questionId: number;
  question: string;
  description: string;
}

export interface TicketRaisePageProps {
  /** Available services grouped by type */
  services: ServiceOption[];
  /** Expert services available for selection */
  expertServices: ExpertServiceOption[];
  /** Active system status messages */
  systemStatusMessages: SystemStatusMessage[];
  /** Whether to prompt user about system status before submitting */
  promptForSystemStatus: boolean;
  /** Customer's name */
  customerName: string;
  /** Customer's email */
  customerEmail: string;
  /** Default proxy name (for subusers) */
  defaultProxyName?: string;
  /** Default proxy email (for subusers) */
  defaultProxyEmail?: string;
  /** Whether support is currently available */
  isInWorkingHours: boolean;
  /** Pre-filled title (for error re-render) */
  prefillTitle?: string;
  /** Pre-filled description (for error re-render) */
  prefillDescription?: string;
  /** Pre-filled service value (for error re-render) */
  prefillService?: string;
  /** Server-side validation error codes: "no_title", "no_service", "no_text" */
  errors?: string[];
  /** HTML content for VPS warning modal */
  vpsWarningHtml?: string;
  /** HTML content for cancellation warning modal */
  cancellationWarningHtml?: string;
  /** Form action URL */
  ticketUrl: string;
  /** Base URL for support question links */
  supportQuestionBaseUrl?: string;
  /** URL for support article search */
  supportListUrl?: string;
  /** URL for duplicate ticket check */
  duplicateCheckUrl?: string;
  /** URL for package setup info */
  packageInfoUrl?: string;
  /** URL for error log retrieval */
  errorLogUrl?: string;
  /** Fetch function for dependency injection */
  fetchFn?: typeof window.fetch;
  /** Custom CSS class */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
}

const ALLOWED_FILE_PATTERN =
  /^(?!.*\.[a-z]{1,4}\.)(?!.*\.\.)(?!.*[^a-z0-9_\s.-]).*[^\s.]+\.(jpe?g|png|txt|log|docx?|pdf)$/i;

const ALLOWED_FILE_TYPES = ['jpg', 'jpeg', 'png', 'txt', 'log', 'doc', 'docx', 'pdf'];

const SERVER_ERROR_MESSAGES: Record<string, { field: string; message: string }> = {
  no_service: { field: 'service', message: 'Please select a service.' },
  no_title: { field: 'title', message: 'Please enter a summary for your issue.' },
  no_text: {
    field: 'description',
    message: 'Please enter a description of at least 50 characters.',
  },
};

function formatCountdown(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins > 0) {
    return `${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
  }
  return `${secs} second${secs !== 1 ? 's' : ''}`;
}

export const TicketRaisePage: React.FC<TicketRaisePageProps> = ({
  services,
  expertServices,
  systemStatusMessages: _systemStatusMessages,
  promptForSystemStatus,
  customerName,
  customerEmail,
  defaultProxyName,
  defaultProxyEmail,
  prefillTitle = '',
  prefillDescription = '',
  prefillService = '',
  errors,
  vpsWarningHtml,
  cancellationWarningHtml,
  ticketUrl,
  supportQuestionBaseUrl = '/manage/support.cgi/q/',
  supportListUrl,
  duplicateCheckUrl,
  packageInfoUrl,
  errorLogUrl,
  fetchFn,
  className = '',
  style,
}) => {
  // Form field state
  const [selectedService, setSelectedService] = useState(prefillService);
  const [title, setTitle] = useState(prefillTitle);
  const [description, setDescription] = useState(prefillDescription);
  const [selectedExpertServices, setSelectedExpertServices] = useState<Set<string>>(new Set());

  // Conditional section visibility
  const [showExpertServices, setShowExpertServices] = useState(false);
  const [showDsPassword, setShowDsPassword] = useState(false);

  // Package features
  const [packageCountdown, setPackageCountdown] = useState<number | null>(null);
  const [errorLog, setErrorLog] = useState('');
  const [showErrorLog, setShowErrorLog] = useState(false);

  // Support articles
  const [supportArticles, setSupportArticles] = useState<SupportArticle[]>([]);
  const [searchingArticles, setSearchingArticles] = useState(false);

  // File upload
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');

  // Validation
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    if (errors) {
      for (const code of errors) {
        const mapping = SERVER_ERROR_MESSAGES[code];
        if (mapping) initial[mapping.field] = mapping.message;
      }
    }
    return initial;
  });

  // Modals
  const [showVpsWarning, setShowVpsWarning] = useState(false);
  const [showCancellationWarning, setShowCancellationWarning] = useState(false);
  const [showAttachmentWarning, setShowAttachmentWarning] = useState(false);
  const [attachmentWarningMessage, setAttachmentWarningMessage] = useState('');
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const vpsWarningShownRef = useRef(false);
  const titleRef = useRef(title);
  // eslint-disable-next-line react-hooks/refs -- intentional: mirror the latest `title` prop into a ref for use in stable callbacks below without recreating them on every render
  titleRef.current = title;

  const fetcher = fetchFn || fetch;

  const submitForm = useCallback(() => {
    (document.getElementById('ticket-raise-form') as HTMLFormElement)?.submit();
  }, []);

  // Derive the selected service option
  const selectedServiceOption = services.find((s) => s.value === selectedService);

  // Map services to FilterableSelect options
  const selectOptions = services.map((s) => ({
    value: s.value,
    label: s.label,
    group: s.group,
  }));

  // --- Callbacks ---

  const validate = useCallback((): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!selectedService) {
      errs.service = 'Please select a service.';
    }
    if (!title.trim()) {
      errs.title = 'Please enter a summary for your issue.';
    }
    if (description.trim().length < 50) {
      errs.description = 'Please enter a description of at least 50 characters.';
    }
    if (showExpertServices && selectedExpertServices.size === 0) {
      errs.expertServices = 'Please select at least one expert service.';
    }
    return errs;
  }, [selectedService, title, description, showExpertServices, selectedExpertServices]);

  const checkDuplicateAndSubmit = useCallback(async () => {
    if (duplicateCheckUrl) {
      try {
        const response = await fetcher(duplicateCheckUrl);
        const data = await response.json();
        if (data?.created_ts) {
          const created = new Date(data.created_ts.replace(/-/g, '/'));
          const diff = (Date.now() - created.getTime()) / 1000;
          if (diff < 60) {
            setShowDuplicateConfirm(true);
            return;
          }
        }
      } catch {
        // Proceed on error
      }
    }
    submitForm();
  }, [duplicateCheckUrl, fetcher, submitForm]);

  const handleSubmit = useCallback(
    async (_data: FormData, _event: React.FormEvent<HTMLFormElement>) => {
      const errs = validate();
      if (Object.keys(errs).length > 0) {
        setValidationErrors(errs);
        return;
      }
      setValidationErrors({});

      // System status confirmation
      if (promptForSystemStatus && selectedServiceOption?.problemServers?.length) {
        setShowStatusConfirm(true);
        return;
      }

      await checkDuplicateAndSubmit();
    },
    [validate, promptForSystemStatus, selectedServiceOption, checkDuplicateAndSubmit]
  );

  const handleStatusConfirm = useCallback(async () => {
    setShowStatusConfirm(false);
    await checkDuplicateAndSubmit();
  }, [checkDuplicateAndSubmit]);

  const handleDuplicateConfirm = useCallback(() => {
    setShowDuplicateConfirm(false);
    submitForm();
  }, [submitForm]);

  const handleServiceChange = useCallback((value: string | null) => {
    setSelectedService(value || '');
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.service;
      return next;
    });
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setFileError('');
      setFileName('');
      return;
    }
    const name = file.name.normalize('NFC');
    if (!ALLOWED_FILE_PATTERN.test(name)) {
      setAttachmentWarningMessage(
        'Only the following file types are allowed: .jpg, .jpeg, .png, .log, .txt, .doc, .docx, .pdf. ' +
          'Please also check for multiple extensions, double periods, periods in the name, or non-alphanumeric characters.'
      );
      setShowAttachmentWarning(true);
      setFileError('Invalid file. Allowed types: .jpg, .jpeg, .png, .log, .txt, .doc, .docx, .pdf');
      e.target.value = '';
      setFileName('');
    } else if (file.size > 1048576) {
      setFileError('File size exceeds 1MB limit.');
      e.target.value = '';
      setFileName('');
    } else {
      setFileError('');
      setFileName(name);
    }
  }, []);

  const handleExpertServiceToggle = useCallback((id: string, checked: boolean) => {
    setSelectedExpertServices((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.expertServices;
      return next;
    });
  }, []);

  // --- Effects ---

  // Service selection change: update conditional sections and fetch data
  /* eslint-disable react-hooks/set-state-in-effect -- intentional: cascade conditional UI state when the selected service changes */
  useEffect(() => {
    const opt = services.find((s) => s.value === selectedService);
    const controller = new AbortController();

    // Expert services
    setShowExpertServices(!!opt?.forExpertServices);
    if (!opt?.forExpertServices) setSelectedExpertServices(new Set());

    // DS password
    setShowDsPassword(opt?.serviceType === 'ds');

    // Reset package features
    setPackageCountdown(null);
    setErrorLog('');
    setShowErrorLog(false);

    // VPS warning (show once per mount)
    if (
      opt?.serviceType === 'vps' &&
      !opt?.managed &&
      !vpsWarningShownRef.current &&
      vpsWarningHtml
    ) {
      setShowVpsWarning(true);
      vpsWarningShownRef.current = true;
    }

    // Cancellation warning
    if (selectedService.startsWith('cancel')) {
      setShowCancellationWarning(true);
    }

    // Package info countdown
    if (opt?.packageId && packageInfoUrl) {
      fetcher(`${packageInfoUrl}?id=${opt.packageId}`, { signal: controller.signal })
        .then((r) => r.json())
        .then((data) => {
          if (data?.ttl_seconds > 0) setPackageCountdown(data.ttl_seconds);
        })
        .catch(() => {});
    }

    // Error log for linux packages
    if (opt?.packageId && opt?.serviceType === 'linux' && errorLogUrl) {
      fetcher(`${errorLogUrl}?package_id=${opt.packageId}`, { signal: controller.signal })
        .then((r) => r.text())
        .then((text) => {
          if (text?.trim()) {
            setErrorLog(text);
            setShowErrorLog(true);
          }
        })
        .catch(() => {});
    }

    return () => controller.abort();
  }, [selectedService, services, packageInfoUrl, errorLogUrl, fetcher, vpsWarningHtml]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Package countdown timer
  useEffect(() => {
    if (packageCountdown === null || packageCountdown <= 0) return;
    const timer = setTimeout(() => {
      setPackageCountdown((prev) => (prev !== null && prev > 1 ? prev - 1 : null));
    }, 1000);
    return () => clearTimeout(timer);
  }, [packageCountdown]);

  // Support article search (debounced)
  useEffect(() => {
    if (!supportListUrl || description.length < 10) {
      /* eslint-disable react-hooks/set-state-in-effect -- intentional: clear support-article state when the search criteria become invalid (description too short / endpoint unconfigured) */
      setSupportArticles([]);
      setSearchingArticles(false);
      /* eslint-enable react-hooks/set-state-in-effect */
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSearchingArticles(true);
      try {
        const params = new URLSearchParams({
          title: titleRef.current,
          body: description,
          'min-score': '3.0',
          limit: '10',
        });
        const response = await fetcher(`${supportListUrl}?${params}`, {
          signal: controller.signal,
        });
        const data = await response.json();
        setSupportArticles(
          Array.isArray(data)
            ? data.slice(0, 5).map((item: Record<string, unknown>) => ({
                questionId: item.question_id as number,
                question: item.question_p as string,
                description: item.description as string,
              }))
            : []
        );
      } catch {
        // Ignore abort and network errors
      } finally {
        if (!controller.signal.aborted) {
          setSearchingArticles(false);
        }
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [description, supportListUrl, fetcher]);

  // --- Render ---

  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const classes = ['hi-ui-ticket-raise-page', className].filter(Boolean).join(' ');

  return (
    <div className={classes} style={style}>
      <Text variant="heading-1">Raise a Support Ticket</Text>

      <Form
        id="ticket-raise-form"
        action={ticketUrl}
        method="post"
        encType="multipart/form-data"
        onSubmit={handleSubmit}
        errors={validationErrors}
        showErrorSummary={hasValidationErrors}
        className="hi-ui-ticket-raise-page__form"
      >
        <input type="hidden" name="action" value="raise_confirm" />

        {/* Service selection */}
        <FormField
          label="Which service does this relate to?"
          id="service-select"
          required
          error={validationErrors.service}
          fullWidth
        >
          <FilterableSelect
            options={selectOptions}
            value={selectedService || undefined}
            onChange={handleServiceChange}
            placeholder="Select a service..."
            name="service"
            fullWidth
          />
        </FormField>

        {/* Expert services */}
        {showExpertServices && expertServices.length > 0 && (
          <div className="hi-ui-ticket-raise-page__expert-services">
            <FormField
              label="Which expert services do you need help with?"
              id="expert-services"
              required
              error={validationErrors.expertServices}
            >
              <Flex
                direction="column"
                gap="sm"
                className="hi-ui-ticket-raise-page__expert-services-list"
              >
                {expertServices.map((es) => (
                  <Checkbox
                    key={es.id}
                    name="expert-services"
                    value={es.id}
                    checked={selectedExpertServices.has(es.id)}
                    onChange={(e) => handleExpertServiceToggle(es.id, e.target.checked)}
                  >
                    {es.name}
                  </Checkbox>
                ))}
              </Flex>
            </FormField>
          </div>
        )}

        {/* Package countdown */}
        {packageCountdown !== null && packageCountdown > 0 && (
          <div className="hi-ui-ticket-raise-page__countdown">
            <Notice variant="info">
              <Text variant="body">
                This package was created less than 1 hour ago and may still be setting up. Please
                allow up to 1 hour for full configuration.
              </Text>
              <Text variant="body" bold className="hi-ui-ticket-raise-page__countdown-timer">
                Time remaining: {formatCountdown(packageCountdown)}
              </Text>
            </Notice>
          </div>
        )}

        {/* Error log */}
        {showErrorLog && errorLog && (
          <div className="hi-ui-ticket-raise-page__error-log">
            <FormField label="Recent error log:" id="error-log">
              <Textarea value={errorLog} readOnly rows={8} fullWidth resize="vertical" />
            </FormField>
          </div>
        )}

        {/* DS password */}
        {showDsPassword && (
          <FormField
            label="Root/administrator password (for dedicated servers):"
            id="ds-password"
            helperText="Providing this allows our team to investigate more quickly."
          >
            <Input type="password" name="service-info-extra" fullWidth />
          </FormField>
        )}

        {/* Summary */}
        <FormField
          label="Summary of your issue:"
          id="ticket-title"
          required
          error={validationErrors.title}
        >
          <Input
            name="ticket_title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setValidationErrors((prev) => {
                const next = { ...prev };
                delete next.title;
                return next;
              });
            }}
            maxLength={60}
            fullWidth
          />
        </FormField>

        {/* Description */}
        <FormField
          label="Please describe your issue in detail:"
          id="ticket-body"
          required
          helperText="Minimum 50 characters"
          error={validationErrors.description}
        >
          <Textarea
            name="ticket_description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setValidationErrors((prev) => {
                const next = { ...prev };
                delete next.description;
                return next;
              });
            }}
            rows={10}
            fullWidth
          />
        </FormField>

        {/* Support articles */}
        {(searchingArticles || supportArticles.length > 0) && (
          <div className="hi-ui-ticket-raise-page__support-articles">
            <Text variant="body" bold>
              You might find these support articles helpful:
            </Text>
            {searchingArticles ? (
              <Flex align="center" gap="sm">
                <Spinner size="small" aria-label="Searching support articles" />
                <Text variant="body-small" inline>
                  Searching...
                </Text>
              </Flex>
            ) : (
              <Flex direction="column" gap="xs" className="hi-ui-ticket-raise-page__article-list">
                {supportArticles.map((article, i) => (
                  <Flex key={article.questionId} gap="sm" align="baseline">
                    <Text variant="body-small" inline>
                      {i + 1}.
                    </Text>
                    <div>
                      <Link href={`${supportQuestionBaseUrl}${article.questionId}`} external>
                        {article.question}
                      </Link>
                      {article.description && (
                        <Text
                          variant="caption"
                          className="hi-ui-ticket-raise-page__article-description"
                        >
                          {article.description}
                        </Text>
                      )}
                    </div>
                  </Flex>
                ))}
              </Flex>
            )}
          </div>
        )}

        {/* Checkboxes */}
        <Flex direction="column" gap="md" className="hi-ui-ticket-raise-page__checkboxes">
          <Checkbox name="authorisation_given" value="yes" defaultChecked>
            I authorise Heart Internet staff to access my services in order to investigate and
            resolve this issue.
          </Checkbox>
          <Checkbox name="ticket_urgent" value="yes">
            <Text variant="inline" bold>
              Urgent:
            </Text>{' '}
            My email or website is completely inaccessible. (Please note: abuse of this option may
            delay your ticket.)
          </Checkbox>
        </Flex>

        {/* File upload */}
        <div className="hi-ui-ticket-raise-page__file-upload">
          <Text variant="body" bold>
            Optional File Attachment
          </Text>
          <Text variant="caption">
            You can upload a screenshot or document to help us resolve your issue. Max file size:
            1MB. Allowed types are:
          </Text>
          <Flex wrap gap="xs" className="hi-ui-ticket-raise-page__file-types">
            {ALLOWED_FILE_TYPES.map((type) => (
              <Badge key={type} size="sm" variant="neutral">
                .{type}
              </Badge>
            ))}
          </Flex>
          <Flex align="center" gap="md" className="hi-ui-ticket-raise-page__file-picker">
            <input
              type="file"
              name="ticket-attachment"
              id="ticket-attachment"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hi-ui-ticket-raise-page__file-input-hidden"
              accept=".jpg,.jpeg,.png,.txt,.log,.doc,.docx,.pdf"
            />
            <Button type="button" variant="tertiary" onClick={() => fileInputRef.current?.click()}>
              Choose file
            </Button>
            <Flex align="center" gap="sm" className="hi-ui-ticket-raise-page__file-info">
              <Text variant="caption" className="hi-ui-ticket-raise-page__file-name">
                {fileName || 'No file chosen'}
              </Text>
              {fileName && (
                <button
                  type="button"
                  className="hi-ui-ticket-raise-page__file-remove"
                  aria-label="Remove file"
                  onClick={() => {
                    fileInputRef.current!.value = '';
                    setFileName('');
                    setFileError('');
                  }}
                >
                  <Icon name="cross" size={14} aria-label="Remove file" />
                </button>
              )}
            </Flex>
          </Flex>
          {fileError && <Notice variant="error">{fileError}</Notice>}
        </div>

        {/* Contact details */}
        <Flex gap="md" className="hi-ui-ticket-raise-page__contact-fields">
          <Flex flex="1">
            <FormField label="Your name:" id="alt-name" fullWidth>
              <Input name="proxy" defaultValue={defaultProxyName || customerName} fullWidth />
            </FormField>
          </Flex>
          <Flex flex="1">
            <FormField label="Contact email:" id="alt-email" fullWidth>
              <Input
                type="email"
                name="additional_email"
                defaultValue={defaultProxyEmail || customerEmail}
                fullWidth
              />
            </FormField>
          </Flex>
        </Flex>

        {/* Submit */}
        <div className="hi-ui-ticket-raise-page__actions">
          <Button type="submit">Submit Ticket</Button>
        </div>
      </Form>

      {/* VPS warning modal */}
      {vpsWarningHtml && (
        <Modal
          isOpen={showVpsWarning}
          onClose={() => setShowVpsWarning(false)}
          title="Important: Virtual Server Support"
          size="medium"
          footer={<Button onClick={() => setShowVpsWarning(false)}>I understand, continue</Button>}
        >
          <div dangerouslySetInnerHTML={{ __html: vpsWarningHtml }} />
        </Modal>
      )}

      {/* Cancellation warning modal */}
      {cancellationWarningHtml && (
        <Modal
          isOpen={showCancellationWarning}
          onClose={() => setShowCancellationWarning(false)}
          title="Cancellation Information"
          size="medium"
          footer={
            <Button onClick={() => setShowCancellationWarning(false)}>
              I understand, continue
            </Button>
          }
        >
          <div dangerouslySetInnerHTML={{ __html: cancellationWarningHtml }} />
        </Modal>
      )}

      {/* Attachment warning modal */}
      <Modal
        isOpen={showAttachmentWarning}
        onClose={() => setShowAttachmentWarning(false)}
        title="Invalid File"
        size="small"
        footer={<Button onClick={() => setShowAttachmentWarning(false)}>OK</Button>}
      >
        <Text variant="body">{attachmentWarningMessage}</Text>
      </Modal>

      {/* Duplicate ticket confirmation */}
      <ConfirmationDialog
        isOpen={showDuplicateConfirm}
        title="Duplicate Ticket?"
        confirmLabel="Submit anyway"
        cancelLabel="Go back"
        onConfirm={handleDuplicateConfirm}
        onCancel={() => setShowDuplicateConfirm(false)}
      >
        <Text variant="body">
          You raised a ticket less than a minute ago. Are you sure you want to submit another one?
        </Text>
      </ConfirmationDialog>

      {/* System status confirmation */}
      <ConfirmationDialog
        isOpen={showStatusConfirm}
        title="Known Server Issues"
        confirmLabel="Submit ticket"
        cancelLabel="Go back"
        onConfirm={handleStatusConfirm}
        onCancel={() => setShowStatusConfirm(false)}
      >
        <Text variant="body">
          The service you have selected is on a server which has a system status message in effect.
          Are you sure you wish to raise a ticket?
        </Text>
      </ConfirmationDialog>

      <div className="hi-ui-ticket-raise-page__back">
        <Link href={`${ticketUrl}/`}>Back to ticket list</Link>
      </div>
    </div>
  );
};

TicketRaisePage.displayName = 'TicketRaisePage';
